import { useFadeIn } from '../../../hooks/useFadeIn';
import { useLang } from '../../../context/LanguageContext';

const reasons = [
  {
    icon: 'ri-message-2-line',
    titleRo: 'Deviz Gratuit în 15 Min',
    titleRu: 'Смета за 15 минут',
    descRo: 'Trimite-ne un mesaj și primești o estimare de preț în 15 minute, fără vizită și fără obligații.',
    descRu: 'Напишите нам и получите оценку стоимости за 15 минут — без выезда и без обязательств.',
  },
  {
    icon: 'ri-award-line',
    titleRo: 'Tehnicieni Certificați',
    titleRu: 'Сертифицированные мастера',
    descRo: 'Toți inginerii sunt certificați Ajax, Hikvision și Bosch. Peste 10 ani experiență.',
    descRu: 'Все специалисты сертифицированы Ajax, Hikvision и Bosch. Более 10 лет опыта.',
  },
  {
    icon: 'ri-shield-star-line',
    titleRo: 'Garanție 3 Ani',
    titleRu: 'Гарантия 3 года',
    descRo: 'Garanție extinsă pe tot echipamentul și service gratuit în perioada de garanție.',
    descRu: 'Расширенная гарантия на всё оборудование и бесплатный сервис в гарантийный период.',
  },
  {
    icon: 'ri-customer-service-2-line',
    titleRo: 'Suport 24/7',
    titleRu: 'Поддержка 24/7',
    descRo: 'Asistență non-stop prin WhatsApp. Intervenție la fața locului în 2h în Chișinău.',
    descRu: 'Круглосуточная поддержка через WhatsApp. Выезд специалиста за 2 часа в Кишинёве.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    titleRo: 'Prețuri Transparente',
    titleRu: 'Прозрачные цены',
    descRo: 'Deviz gratuit, fără costuri ascunse. Prețul agreat este cel final — zero surprize.',
    descRu: 'Бесплатная смета, без скрытых расходов. Согласованная цена — окончательная.',
  },
  {
    icon: 'ri-smartphone-line',
    titleRo: 'Control din Telefon',
    titleRu: 'Управление с телефона',
    descRo: 'Monitorizare live, armare/dezarmare, notificări instant din aplicația mobilă.',
    descRu: 'Живой мониторинг, постановка/снятие с охраны, уведомления — всё через приложение.',
  },
];

const testimonials = [
  {
    name: 'Alexandru Munteanu',
    roleRo: 'Proprietar casă, Chișinău',
    roleRu: 'Владелец дома, Кишинёв',
    textRo: 'Am instalat Ajax la casa mea. Totul a funcționat perfect din prima zi. Tehnicianul a explicat fiecare detaliu.',
    textRu: 'Установили Ajax в моём доме. Всё работает отлично с первого дня. Мастер объяснил каждую деталь.',
    stars: 5,
  },
  {
    name: 'Ирина Попеску',
    roleRo: 'Proprietară apartament, Chișinău',
    roleRu: 'Владелица квартиры, Кишинёв',
    textRo: 'Excelent! Au instalat camere Hikvision într-o zi. Acum văd totul pe telefon, de oriunde.',
    textRu: 'Отлично! Установили камеры Hikvision за один день. Теперь вижу всё через телефон, откуда угодно.',
    stars: 5,
  },
  {
    name: 'Vasile Rusu',
    roleRo: 'Director, SRL TechPro',
    roleRu: 'Директор, ООО TechPro',
    textRo: 'Am echipat întreg biroul cu Ajax și Dahua. Profesionalism maxim, livrare în termen.',
    textRu: 'Оборудовали весь офис Ajax и Dahua. Максимальный профессионализм, сдача в срок.',
    stars: 5,
  },
];

export default function WhyChooseUs() {
  const ref = useFadeIn<HTMLElement>();
  const { t } = useLang();

  return (
    <section id="why-us" ref={ref} className="section-fade py-24 bg-charcoal-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon/40 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="slide-in-left inline-block px-4 py-1 rounded-full border border-neon/30 bg-neon/5 text-neon text-xs font-inter tracking-widest uppercase mb-4">
            {t('De Ce Noi', 'Почему мы')}
          </span>
          <h2 className="font-rajdhani font-bold text-4xl md:text-5xl text-white">
            {t('Alegerea Inteligentă', 'Умный выбор')}
          </h2>
          <p className="font-inter text-cyber-muted mt-3 text-base max-w-lg mx-auto">
            {t(
              'Seriozitate, echipament de top și garanție reală — nu promisiuni.',
              'Надёжность, оборудование топ-класса и реальная гарантия — не обещания.'
            )}
          </p>
          <div className="mt-5 mx-auto w-16 h-0.5 bg-neon rounded-full line-grow"></div>
        </div>

        {/* Reasons grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {reasons.map((reason, idx) => (
            <div
              key={reason.titleRo}
              className="group stagger-item p-5 rounded-lg border bg-charcoal transition-all duration-300 card-hover-glow cursor-default"
              style={{ borderColor: 'rgba(0,255,136,0.12)' }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-md bg-neon/10 border border-neon/20 mb-4 transition-all duration-300 group-hover:bg-neon/15 group-hover:border-neon/40">
                <i className={`${reason.icon} text-xl text-neon`}></i>
              </div>
              <h3 className="font-rajdhani font-bold text-lg text-white mb-2">{t(reason.titleRo, reason.titleRu)}</h3>
              <p className="font-inter text-sm text-cyber-muted leading-relaxed">{t(reason.descRo, reason.descRu)}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div>
          <h3 className="font-rajdhani font-bold text-2xl text-center text-white mb-2">
            {t('Ce spun clienții noștri', 'Что говорят наши клиенты')}
          </h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {testimonials.map((item) => (
              <div
                key={item.name}
                className="stagger-item p-5 rounded-lg border bg-charcoal transition-all duration-300 card-hover-glow"
                style={{ borderColor: 'rgba(0,255,136,0.1)' }}
              >
                <div className="flex mb-3">
                  {Array.from({ length: item.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-neon text-sm"></i>
                  ))}
                </div>
                <p className="font-inter text-sm text-cyber-text leading-relaxed mb-4">
                  &ldquo;{t(item.textRo, item.textRu)}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-neon/10 border border-neon/20">
                    <i className="ri-user-3-line text-neon text-sm"></i>
                  </div>
                  <div>
                    <div className="font-rajdhani font-bold text-white text-sm">{item.name}</div>
                    <div className="font-inter text-xs text-cyber-muted">{t(item.roleRo, item.roleRu)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA banner */}
        <div className="mt-14 p-8 rounded-xl border border-neon/20 bg-charcoal text-center relative overflow-hidden">
          <div className="absolute inset-0 cyber-grid-bg opacity-20"></div>
          <div className="relative z-10">
            <h3 className="font-rajdhani font-bold text-3xl text-white mb-2">
              {t('Ești gata să-ți securizezi casa?', 'Готовы защитить свой дом?')}
            </h3>
            <p className="font-inter text-cyber-muted mb-6 text-sm max-w-md mx-auto">
              {t(
                'Scriem-ne pe WhatsApp și primești un deviz gratuit în 15 minute — fără vizită, fără obligații.',
                'Напишите нам в WhatsApp и получите бесплатную смету за 15 минут — без выезда, без обязательств.'
              )}
            </p>
            <a
              href="https://wa.me/37360123456?text=Buna%20ziua!%20Doresc%20un%20deviz%20gratuit."
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2.5 px-7 sm:px-10 py-3.5 sm:py-4 rounded-md bg-neon text-charcoal font-rajdhani font-bold text-sm sm:text-base md:text-xl btn-neon transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg sm:text-2xl"></i>
              {t('Cere Deviz Gratuit — WhatsApp', 'Запросить смету — WhatsApp')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
